import { cast, flow, getParent, Instance, types } from 'mobx-state-tree';
import { CoreApi, RegionalApi } from 'service/axios-api';
import { IProductLibrary } from './product-library';

const SupportedOS = types
    .model('SupportedOS', {
        windowsVista: types.optional(types.boolean, false, [null, undefined]),
        windows7: types.optional(types.boolean, false, [null, undefined]),
        windows8: types.optional(types.boolean, false, [null, undefined]),
        windows10: types.optional(types.boolean, false, [null, undefined]),
    })
    .views((self) => ({
        get toStringArray(): string[] {
            const os = [];
            if (self.windowsVista) os.push('Windows Vista');
            if (self.windows7) os.push('Windows 7');
            if (self.windows8) os.push('Windows 8');
            if (self.windows10) os.push('Windows 10');
            return os;
        },
    }));

const SupportedArch = types
    .model('SupportedArch', {
        bit32: types.optional(types.boolean, false, [null, undefined]),
        bit64: types.optional(types.boolean, false, [null, undefined]),
    })
    .views((self) => ({
        get toStringArray(): string[] {
            const arch = [];
            if (self.bit32) arch.push('32-bit');
            if (self.bit64) arch.push('64-bit');
            return arch;
        },
    }));

// /game-build/v1/configuration/games/{productID}
export const DetailAPI = types
    .model('ProductAPI', {
        public: types.boolean,
        shortCutName: types.string,
        directoryName: types.string,
        patch: types.boolean,
        executablePath: types.string,
        executablePathBit64: types.optional(types.string, '', [null]),
        parameter: types.optional(types.array(types.string), [], [null, undefined]),
        workingDirectory: types.string,
        requiredUac: types.boolean,
        supportAutomaticUpdate: types.boolean,
        requiredInstallationSoftware: types.array(types.string),
        requiredDiskSpace: types.optional(types.string, '', [null]),
        supportedOs: SupportedOS,
        supportedSystemType: SupportedArch,
    })
    .views((self) => ({
        get parent(): IProductLibrary {
            return getParent<IProductLibrary>(self);
        },
    }))
    .views((self) => ({
        get coreFormat(): { productId: string; name: string; deactiveDiffPatch: boolean; displayName: string } {
            const data = {
                productId: self.parent.productId,
                name: self.directoryName,
                deactiveDiffPatch: self.patch === false,
                displayName: self.parent.displayName,
            };
            return data;
        },
    }));

// /game-user/v1/access?product_id=
export const AccessAPI = types.model('AccessAPI', {
    ipBlocked: types.optional(types.boolean, false),
    isPlayable: types.optional(types.boolean, false),
    isDeveloper: types.optional(types.boolean, false),
    isMinor: types.optional(types.boolean, false),
});

export interface IProductCommon extends Instance<typeof ProductCommon> {}
export const ProductCommon = types
    .model('ProductCommon', {
        productId: types.identifier,
        access: types.maybe(AccessAPI),
        parentEmail: types.optional(types.string, ''),
    })
    .volatile((self) => ({
        playableData: {},
        error: {},
    }))
    .actions((self) => {
        const actions = {
            fetchAccess: flow(function* () {
                const { data } = yield CoreApi.post('/game-auth2/v1/access', { productId: self.productId });
                self.access = cast(data);
                return data;
            }),
            requestParentPIN: flow(function* () {
                const { email }: { email: string } = yield RegionalApi.post('/user/restriction/request-code');
                self.parentEmail = email.replace(/^(.)(.*)(.@.*)$/, (_, a, b, c) => a + b.replace(/./g, '*') + c); // convert to masked email
            }),
            async verifyParentPIN(code: string, rememberMe = false) {
                return await CoreApi.post('/game-auth2/v1/playable', {
                    productId: self.productId,
                    code,
                    rememberMe,
                });
            },
        };
        return actions;
    });
